import React, { JSX, useState } from 'react'
import { PAGE_SIZES } from 'lib/adminListState'

export interface ToolbarChip {
  key: string
  label: string
  count?: number | null
  active: boolean
}

export const ChipRow = ({ chips, onChip, label, testid }: {
  chips: ToolbarChip[]
  onChip: (key: string) => void
  label: string
  testid?: string
}): JSX.Element => (
  <div role='group' aria-label={label} className='flex flex-wrap items-center gap-1.5' data-testid={testid}>
    {chips.map((chip) => (
      <button
        key={chip.key}
        type='button'
        onClick={() => onChip(chip.key)}
        aria-pressed={chip.active}
        data-testid={testid != null ? `${testid}-${chip.key}` : undefined}
        className={chip.active
          ? 'inline-flex items-center gap-1.5 rounded-md bg-brand/15 px-2 py-1 text-[11px] font-medium text-brand ring-1 ring-inset ring-brand/40'
          : 'inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'}
      >
        {chip.label}
        {chip.count != null && (
          <span className='inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white/10 px-1 text-[10px] font-semibold tabular-nums'>
            {chip.count}
          </span>
        )}
      </button>
    ))}
  </div>
)

export const PageSizePicker = ({ value, onChange, testid }: {
  value: number
  onChange: (size: number) => void
  testid?: string
}): JSX.Element => (
  <label className='inline-flex items-center gap-1.5 text-[11px] text-dm-text-accent'>
    Rows
    <select
      value={value}
      onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
      aria-label='Rows per page'
      data-testid={testid}
      className='rounded-md border border-white/10 bg-dm-bg-accent/40 px-1.5 py-1 text-[11px] text-dm-text-primary focus:border-brand focus:outline-none'
    >
      {PAGE_SIZES.map((size) => (
        <option key={size} value={size}>{size}</option>
      ))}
    </select>
  </label>
)

export const SearchBox = ({ placeholder, label, activeTerm, onSubmit, testid }: {
  placeholder: string
  label: string
  activeTerm: string | null
  onSubmit: (term: string | null) => void
  testid?: string
}): JSX.Element => {
  const [value, setValue] = useState('')
  return (
    <form
      className='flex items-center gap-1.5'
      onSubmit={(e) => {
        e.preventDefault()
        const trimmed = value.trim()
        onSubmit(trimmed === '' ? null : trimmed)
      }}
    >
      <input
        type='search'
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        autoComplete='off'
        spellCheck={false}
        data-testid={testid}
        className='w-56 rounded-md border border-white/10 bg-dm-bg-accent/40 px-2.5 py-1.5 text-xs text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none'
      />
      <button
        type='submit'
        data-testid={testid != null ? `${testid}-go` : undefined}
        className='rounded-md border border-white/10 px-2.5 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
      >
        Search
      </button>
      {activeTerm != null && (
        <button
          type='button'
          onClick={() => {
            setValue('')
            onSubmit(null)
          }}
          data-testid={testid != null ? `${testid}-clear` : undefined}
          className='rounded-md border border-white/10 px-2.5 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
        >
          Clear
        </button>
      )}
    </form>
  )
}

export const AdminListToolbar = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <div className='mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2'>
    {children}
  </div>
)
